import moment from "moment";
import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import styled, { withTheme } from "styled-components";
import { currentStudy } from "../../../config";
import {
  aggregateAll,
  calculateAvg,
  calculateKcal,
  calculateKcalAndPercentage,
  calculateMealPercentages,
  decimalPlaces,
  generateWeeks,
  isBetweenDates,
  roundMealValue,
} from "../../../helpers/mealsHelper";
import { DateFormat } from "../../../modules/constants";
import { exportCSVFile, getDates, pdfDownload } from "../../../modules/utils";
import AnimatedButton from "../../ui-library/Buttons/AnimatedButton/AnimatedButton";
import CustomDropdown from "../../ui-library/CustomDropdown/CustomDropdown";
import DateSelect from "../../ui-library/DateAndTime/Date/DateSelect";
import NutritionStabilizationReport from "../reports/NutritionStabilizationReport/NutritionStabilizationReport";
import { reportTypes } from "../../../modules/constants";

const Label = styled.span`
  color: ${(props) => props.theme?.mortar};
  font-size: 15px;
  font-weight: 600;
`;

const DatePickerWrapper = styled.div`
  div.betweenDays.react-datepicker__day--disabled
    ~ div:not(.betweenDays):not(.react-datepicker__day--disabled):not(:hover) {
    background-color: ${(props) => props?.theme?.lightPurple};
    border-radius: 0.3rem;
  }

  div.betweenDays.react-datepicker__day--disabled:not(.react-datepicker__day--selected) {
    background-color: ${(props) => props?.theme?.lightPurple};
    border-radius: 0.3rem;
  }

  div[class~="react-datepicker__day--in-selecting-range"]:not(.react-datepicker__day--selected) {
    background-color: ${(props) => props?.theme?.lightPurple};
    border-radius: 0.3rem;
  }

  .react-datepicker__day--range-start {
    background-color: ${(props) => props?.theme?.darkPurple};
  }

  div.react-datepicker__day--in-selecting-range:hover {
    background-color: ${(props) => props?.theme?.darkPurple};
  }

  .react-datepicker-popper {
    position: relative !important;
    transform: none !important;
    width: 240px;
    height: 490px;
  }

  @media (max-width: 320px) {
    .react-datepicker__input-container div {
      padding-left: 1.8rem;
      padding-right: 1.8rem;
    }

    .react-datepicker__input-container div i:first-child {
      left: 5px;
    }
  }
`;

const ActionButtonsContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  margin-top: 38px;
  @media (max-width: 576px) {
    padding-top: 2rem;
    margin-top: auto;
    .btn {
      width: 100%;
    }
  }
`;

const StyledError = styled.div`
  font-weight: 600;
  font-size: 12px;
  color: ${(props) => props.theme?.cardinal};
  text-transform: uppercase;
`;

const ReportGeneratorOptions = (props) => {
  const { fetchEntriesData, participant } = props;
  const [selectedReportType, setSelectedReportType] = useState(null);
  const { t } = useTranslation();
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [showErrorDate, setShowErrorDate] = useState(false);
  const [isValidDate, setIsValidDate] = useState(true);
  const [availableDates, setAvailableDates] = useState([]);
  const [hoveredDate, setHoveredDate] = useState("");
  const [errors, setErrors] = useState({});

  const [isGenerateReportSuccess, setGenerateReportSuccess] = useState(false);
  const [isGenerateReportError, setGenerateReportError] = useState(false);

  const btnGenerateReportErrorMngmt = {
    isSuccess: isGenerateReportSuccess,
    setIsSuccess: setGenerateReportSuccess,
    isError: isGenerateReportError,
    setIsError: setGenerateReportError,
  };

  const headers = useMemo(() => {
    return {
      date: " ",
      calories: "Calories (kcal)",
      protein: "Protein (%)",
      fats: "Fats (%)",
      carbohydrates: "Carbs (%)",
      sugar: "Simple sugars (g)",
      iscomplete: "Complete day",
      fastingDay: "Fasting day",
    };
  }, []);

  const reportTypeIsPdf = useMemo(() => {
    return selectedReportType?.id === reportTypes.PDF.id;
  }, [selectedReportType]);

  const isDateRangeValid = useMemo(() => {
    if (startDate !== "" && endDate !== "") {
      if (reportTypeIsPdf) {
        return moment(endDate).diff(moment(startDate), "week") + 1 >= 4;
      }
      return true;
    }
    return false;
  }, [startDate, endDate, reportTypeIsPdf]);

  const isReportTypeValid = useMemo(() => {
    return !!selectedReportType;
  }, [selectedReportType]);

  const generateNutricionReport = () => {
    validateReportType();
    validateDateRange();

    if (isDateRangeValid) {
      if (reportTypeIsPdf) {
        generatePDF();
      } else if (selectedReportType?.id === reportTypes.CSV_DAILY_AVERAGES.id) {
        generateCSV();
      }
    } else {
      setGenerateReportError(true);
    }
  };

  const generateCSV = () => {
    const start = moment(startDate)
      .startOf("day")
      .format(DateFormat.START_DATE);
    const end = moment(endDate).endOf("day").format(DateFormat.END_DATE);

    fetchEntriesData(start, end, participant.participantId).then(
      (reportData) => {
        exportCSVFile(
          headers,
          formatDataForCSV(reportData, start, end),
          reportData.generatedDate,
          participant.userName,
          currentStudy()
        ).then(() => {
          setGenerateReportSuccess(true);
        });
      }
    );
  };

  const aggregatorByDay = (accumulator, item) => {
    const meal = JSON.parse(item.mealJson).aboutMeal;

    const date = moment(item.mealDate)
      .format(DateFormat.BASE_FORMAT)
      .toString();
    let previousDate = accumulator[date];
    if (!previousDate) {
      previousDate = accumulator;
    }

    return {
      ...accumulator,
      [date]: {
        date: date,
        completed: item.completed,
        calories: {
          value: (previousDate?.calories?.value ?? 0) + meal?.Calories?.value,
        },
        protein: {
          value: (previousDate?.protein?.value ?? 0) + meal?.Protein?.value,
        },
        carbohydrates: {
          value: (previousDate?.carbohydrates?.value ?? 0) + meal.Carbs.value,
        },
        fats: {
          value: (previousDate?.carbohydrates?.value ?? 0) + meal?.Carbs?.value,
        },
        sugars: {
          value: (previousDate?.sugars?.value ?? 0) + meal?.Sugars?.value,
        },
      },
    };
  };

  const generatePDF = () => {
    const start = moment(startDate)
      .startOf("day")
      .format(DateFormat.START_DATE);
    const end = moment(endDate).endOf("day").format(DateFormat.END_DATE);

    fetchEntriesData(start, end, participant.participantId).then(
      (reportData) => {
        const data = parseTableData(
          reportData.entries,
          generateWeeks(start, end)
        );
        data.generatedDate = reportData.generatedDate;
        data.participantID = participant.userName;

        const dateRange = `${moment(start).format(
          DateFormat.BASE_FORMAT
        )} to ${moment(end).format(DateFormat.BASE_FORMAT)}`;
        data.totalWeeks.dateRange = dateRange;
        data.currentStudy = currentStudy();

        pdfDownload(
          <NutritionStabilizationReport {...data} />,
          `${data.participantID}-${data.generatedDate}`
        )
          .then(() => {
            setGenerateReportSuccess(true);
          })
          .catch(() => {
            setGenerateReportError(true);
          });
      }
    );
  };

  const roundNumber = (number, unit, decimalPlaces) => {
    if (Number(number).toFixed(decimalPlaces) === "NaN") {
      return t("appText.fastingDayLabel");
    }
    return `${Number(number).toFixed(decimalPlaces)} ${unit ? unit : ""}`;
  };

  const getAverage = (data) => {
    const initialValue = {
      date: `AVG ${moment(startDate).format(DateFormat.BASE_FORMAT)} - ${moment(
        endDate
      ).format(DateFormat.BASE_FORMAT)}`,
      calories: {
        value: 0,
      },
      protein: {
        percentage: 0,
      },
      fats: {
        percentage: 0,
      },
      carbohydrates: {
        percentage: 0,
      },
      sugars: {
        value: 0,
      },
    };
    //Aggregate all values and calculate the average
    const avg = calculateAvg(
      data
        .filter((item) => item.isComplete === "Y")
        .reduce(aggregateAll, initialValue)
    );

    const finalAvg = {
      ...avg,
      calories: roundNumber(avg.calories.value, null, decimalPlaces),
      protein: roundNumber(avg.protein.value, "%", decimalPlaces),
      fats: roundNumber(avg.fats.value, "%", decimalPlaces),
      carbohydrates: roundNumber(
        avg.carbohydrates.percentage,
        "%",
        decimalPlaces
      ),
      sugars: roundNumber(avg.sugars.value, "g", decimalPlaces),
      completed: "\r\n",
    };

    return finalAvg;
  };

  const formatDataForCSV = (rawData, startDate, endDate) => {
    const formatattedData = rawData.entries.total.reduce(aggregatorByDay, {});

    const kcalData = Object.values(formatattedData).map((item) => {
      return calculateKcal(item);
    });

    const finalData = [];

    const meals = Object.values(kcalData)
      .map((item) => calculateMealPercentages(item))
      .map((item) => {
        return {
          date: item.date,
          calories: item.completed
            ? `${Number(roundMealValue(item.calories.value)).toFixed(
                decimalPlaces
              )}`
            : "",
          protein: item.completed
            ? `${Number(roundMealValue(item.protein.percentage)).toFixed(
                decimalPlaces
              )} %`
            : "",
          fats: item.completed
            ? `${Number(roundMealValue(item.fats.percentage)).toFixed(
                decimalPlaces
              )} %`
            : "",
          carbohydrates: item.completed
            ? `${Number(roundMealValue(item.carbohydrates.percentage)).toFixed(
                decimalPlaces
              )} %`
            : "",
          sugars: item.completed
            ? `${Number(roundMealValue(item.sugars.value)).toFixed(
                decimalPlaces
              )} g`
            : "",
          isComplete: item.completed ? "Y" : "N",
        };
      });

    finalData.push(getAverage(meals));

    const dateRange = getDates(new Date(startDate), new Date(endDate));
    dateRange.forEach((date) => {
      const meal = meals.find((item) => item.date === date);
      if (meal) {
        finalData.push(meal);
      } else {
        finalData.push({
          date: moment(date).format(DateFormat.BASE_FORMAT),
          calories: t("appText.fastingDayLabel"),
          protein: t("appText.fastingDayLabel"),
          fats: t("appText.fastingDayLabel"),
          carbohydrates: t("appText.fastingDayLabel"),
          sugars: t("appText.fastingDayLabel"),
          isComplete: t("appText.fastingDayLabel"),
        });
      }
    });

    return finalData;
  };

  const parseTableData = (data, weeks) => {
    const { total } = data;
    const avgPerWeekTotal = [];
    const initialValue = {
      calories: {
        value: 0,
        unit: "",
      },
      protein: {
        percentage: 0,
        value: 0,
        unit: "",
      },
      carbohydrates: {
        percentage: 0,
        value: 0,
        unit: "",
      },
      fats: {
        percentage: 0,
        value: 0,
        unit: "",
      },
      sugars: {
        value: 0,
        unit: "",
      },
      completed: 0,
    };

    if (total) {
      const totalPerday = total
        .filter((meal) => meal.completed)
        .reduce(aggregatorByDay, {});

      const meals = Object.values(totalPerday).map((item) =>
        calculateKcalAndPercentage(item)
      );

      weeks.forEach((week) => {
        const totalPerWeek = meals
          .filter((meal) => meal.completed && isBetweenDates(week, meal.date))
          .reduce(aggregateValues, initialValue);

        const avgPerWeek = calculateAvg(totalPerWeek);

        avgPerWeek.calories.value = Number(
          roundMealValue(avgPerWeek.calories.value).toFixed(decimalPlaces)
        );
        avgPerWeek.carbohydrates.percentage = Number(
          roundMealValue(avgPerWeek.carbohydrates.percentage).toFixed(
            decimalPlaces
          )
        );
        avgPerWeek.fats.percentage = Number(
          roundMealValue(avgPerWeek.fats.percentage).toFixed(decimalPlaces)
        );
        avgPerWeek.protein.percentage = Number(
          roundMealValue(avgPerWeek.protein.percentage).toFixed(decimalPlaces)
        );
        avgPerWeek.sugars.value = Number(
          roundMealValue(avgPerWeek.sugars.value).toFixed(decimalPlaces)
        );

        const formatWeek = `${moment(week[0]).format(
          DateFormat.BASE_FORMAT
        )} to ${moment(week[1]).format(DateFormat.BASE_FORMAT)}`;

        avgPerWeekTotal.push({
          week: formatWeek,
          sumPerWeek: avgPerWeek,
        });
      });
    }

    const selectedWeeks = avgPerWeekTotal.reduce(
      (prev, curr) => (curr.sumPerWeek.completed > 0 ? ++prev : prev),
      0
    );

    //Calculate de date range average
    const sumAllWeeks = calculateAvg(
      avgPerWeekTotal.reduce(aggregateAllWeeks, initialValue),
      selectedWeeks
    );

    return { totalPerWeek: avgPerWeekTotal, totalWeeks: sumAllWeeks };
  };

  const aggregateValues = (accumulator, item) => {
    const accumulatorDate = moment(accumulator.date).format(
      DateFormat.INVERTED_DATE
    );
    const mealDate = moment(item.date).format(DateFormat.INVERTED_DATE);

    const completed =
      accumulatorDate === mealDate
        ? accumulator.completed
        : accumulator.completed + 1;

    return {
      ...item,
      date: item.date,
      completed,

      protein: {
        value: accumulator.protein.value + item.protein.value,
        percentage: accumulator.protein.percentage + item.protein.percentage,
      },
      carbohydrates: {
        value: accumulator.carbohydrates.value + item.carbohydrates.value,
        percentage:
          accumulator.carbohydrates.percentage + item.carbohydrates.percentage,
      },
      fats: {
        value: accumulator.fats.value + item.fats.value,
        percentage: accumulator.fats.percentage + item.fats.percentage,
      },
      sugars: {
        value: accumulator.sugars.value + item.sugars.value,
      },
    };
  };

  const aggregateAllWeeks = (accumulator, item) => {
    const { sumPerWeek } = item;

    return {
      completed: accumulator.completed + sumPerWeek.completed,
      calories: {
        value: accumulator.calories.value + sumPerWeek.calories.value,

        unit: sumPerWeek.calories.unit,
      },
      protein: {
        value: accumulator.protein.value + sumPerWeek.protein.value,
        unit: sumPerWeek.protein.unit,
        kcal: accumulator.protein.kcal + sumPerWeek.protein.kcal,
        percentage:
          accumulator.protein.percentage + sumPerWeek.protein.percentage,
      },
      carbohydrates: {
        value: accumulator.carbohydrates.value + sumPerWeek.carbohydrates.value,
        unit: sumPerWeek.carbohydrates.unit,
        kcal: accumulator.carbohydrates.kcal + sumPerWeek.carbohydrates.kcal,
        percentage:
          accumulator.carbohydrates.percentage +
          sumPerWeek.carbohydrates.percentage,
      },
      fats: {
        value: accumulator.fats.value + sumPerWeek.fats.value,
        unit: sumPerWeek.fats.unit,
        kcal: accumulator.fats.kcal + sumPerWeek.fats.kcal,
        percentage: accumulator.fats.percentage + sumPerWeek.fats.percentage,
      },
      sugars: {
        value: accumulator.sugars.value + sumPerWeek.sugars.value,
        unit: sumPerWeek.sugars.unit,
      },
    };
  };

  const handleReportTypeOnChange = (value) => {
    if (value) {
      setSelectedReportType(value);
    }

    setStartDate("");
    setEndDate("");
    setAvailableDates([]);
    setHoveredDate("");
    setErrors(null);
    setGenerateReportError(true);
  };

  const errorHandling = {
    showErrorDate: showErrorDate,
    setShowErrorDate: setShowErrorDate,
    isValidDate: isValidDate,
    setIsValidDate: setIsValidDate,
  };

  const generateDates = (startDate) => {
    const weeks = 9;
    const weeksDays = 6;
    const date = new Date(startDate.getTime());
    const limitDate = date.setDate(
      new Date(date).getDate() + weeks * weeksDays + 1
    );
    return generateWeeks(startDate, limitDate);
  };

  const onDateChange = (dates) => {
    const [start, end] = dates;

    if (start && !end) {
      const maxWeeks = generateDates(start);
      setAvailableDates(maxWeeks);
    }

    if (start && end) {
      setStartDate(start);
      setEndDate(end);
    }
  };

  //Check if the provided date is the last day of the week
  const filterDates = (date) => {
    if (availableDates.length > 0) {
      return availableDates
        .map((availableDate) => availableDate[1])
        .includes(moment(date).format(DateFormat.INVERTED_DATE));
    }
    return true;
  };

  //Reset calendar state
  const onCalendarClose = () => {
    if (startDate && !endDate) {
      onDateChange([startDate, startDate]);
    }
  };

  //Add custom class to all days between the start day and the hovered day
  const renderClass = (date) => {
    if (
      reportTypeIsPdf &&
      !filterDates(date) &&
      date > startDate &&
      hoveredDate >= date
    ) {
      return "betweenDays";
    }
    return null;
  };

  //Get the day that we are hovering with the mouse
  const onDayMouseEnter = (date) => {
    if (reportTypeIsPdf) {
      setHoveredDate(date);
    }
  };

  const validateReportType = useCallback(
    (value) => {
      if (!isReportTypeValid) {
        setGenerateReportError(true);
      }
      setErrors((prevState) => ({
        ...prevState,
        reportType: isReportTypeValid
          ? undefined
          : "Please select a report type.",
      }));
    },
    [isReportTypeValid]
  );

  const getErrorMsg = useMemo(() => {
    if (reportTypeIsPdf) {
      return "Please select a date range with a minimum of 4 weeks";
    } else {
      return "Please select a date range";
    }
  }, [reportTypeIsPdf]);

  const validateDateRange = useCallback(
    (value) => {
      setErrors((prevState) => ({
        ...prevState,
        dateRange: isDateRangeValid ? undefined : getErrorMsg,
      }));
    },
    [getErrorMsg, isDateRangeValid]
  );

  return (
    <div data-testid="nutrition-report">
      <div className="mb-4">
        <Label className="mb-1">Select report type:</Label>
        <CustomDropdown
          placeholder="Select"
          values={Object.values(reportTypes)}
          onChange={(e) => handleReportTypeOnChange(e)}
          width={200}
        />
        <StyledError className="mt-2">
          {errors?.reportType && <span>{errors?.reportType}</span>}
        </StyledError>
      </div>

      {selectedReportType && (
        <div>
          <Label htmlFor="dateselect" className="mb-1">
            Date range:
          </Label>
          <DatePickerWrapper>
            <DateSelect
              data-testid="sted"
              eventDate={startDate}
              setEventDate={setStartDate}
              startDate={startDate}
              setStartDate={setStartDate}
              endDate={endDate}
              setEndDate={setEndDate}
              errorHandling={errorHandling}
              monthsShown={2}
              selectsRange={true}
              displayTitle={false}
              maxDate={new Date()}
              onCalendarClose={onCalendarClose}
              onChange={onDateChange}
              dayClassName={(date) => renderClass(date)}
              filterDate={reportTypeIsPdf ? filterDates : undefined}
              onDayMouseEnter={onDayMouseEnter}
              onCalendarOpen={() => handleReportTypeOnChange(null)}
            />
          </DatePickerWrapper>
          <StyledError>
            {errors?.dateRange && <span>{errors?.dateRange}</span>}
          </StyledError>
        </div>
      )}

      <ActionButtonsContainer>
        <AnimatedButton
          data-testid="generate-report"
          onClick={generateNutricionReport}
          icon={null}
          {...btnGenerateReportErrorMngmt}
        >
          Generate Report
        </AnimatedButton>
      </ActionButtonsContainer>
    </div>
  );
};

export default withTheme(ReportGeneratorOptions);
